import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { apiFetchAuth } from '../../api/client';
import { isLoggedIn } from '../../auth/auth';
import { requireLoginOrContinue } from '../../auth/returnTo';
import { useContent } from '../../content';
import {
    analyzePotential,
    calculateIdentityDot,
    calculateMatchStatus,
    calculateStyleField,
    checkTargetFeasibility,
    generateActionPlan,
    generateMatchInsight,
    getChugumiCoordinate,
} from '../../utils/styleFieldLogic';
import PersonaHero from './faceReport/PersonaHero';
import VisualDNASection from './faceReport/VisualDNASection';
import VibeAnalysisSection from './faceReport/VibeAnalysisSection';
import PremiumActionSection from './faceReport/PremiumActionSection';
import MasculinityDetailSection from './faceReport/MasculinityDetailSection';
import { InjectAnimations, styles } from './FaceReportStyles';
import { clearStoredFaceResult, readStoredFaceResult, writeStoredFaceResult } from '../../utils/faceResultStorage';
import { formatResultDate } from '../../utils/dateFormat';
import { getScoreBucket, trackEvent, trackEventOnce } from '../../utils/analytics';
import { useReportAnalytics } from '../../hooks/useReportAnalytics';
import { localizeFacePersona } from '../../i18n/facePersona';

const toSentences = (text) => {
    if (!text || typeof text !== 'string') return [];
    return text
        .replace(/\*\*/g, '')
        .split(/[.\n]/)
        .map((line) => line.trim())
        .filter(Boolean);
};

const firstSentence = (...texts) => {
    for (const text of texts) {
        const sentence = toSentences(text)[0];
        if (sentence) return sentence;
    }
    return '';
};

const FirstImpressionSlide = ({ result }) => {
    const { t } = useTranslation();
    const persona = result?.persona || {};
    const items = [
        {
            title: t('face_result.detail.impression.combined'),
            body: persona?.analysis_report?.first_impression || firstSentence(persona?.description) || t('face_result.detail.impression.comfortable_fallback'),
        },
        {
            title: t('face_result.detail.impression.evidence'),
            body: persona?.analysis_report?.evidence_summary || firstSentence(persona?.analysis_report?.spice_comment) || t('face_result.detail.impression.memorable_fallback'),
        },
        {
            title: t('face_result.detail.impression.approachability'),
            body: persona?.approachability?.desc || t('face_result.detail.impression.reserved_fallback'),
        },
    ];

    return (
        <div style={slideStackStyle}>
            <div style={sectionCardStyle}>
                <div style={eyebrowStyle}>{t('face_result.detail.impression.eyebrow')}</div>
                <div style={headlineStyle}>{t('face_result.detail.impression.headline')}</div>
                <p style={bodyStyle}>
                    {t('face_result.detail.impression.description')}
                </p>
            </div>

            {items.map((item, index) => (
                <div key={item.title} style={sectionCardStyle}>
                    <div style={numberLabelStyle}>{index + 1}</div>
                    <div style={miniHeadlineStyle}>{item.title}</div>
                    <p style={bodyStyle}>{item.body}</p>
                </div>
            ))}
        </div>
    );
};

const UsageGuideSlide = ({
    result,
    hasIntegrated,
    physical,
    target,
    matchType,
    congruenceScore,
    chugumiResult,
    handleIntegrate,
    integrating,
}) => {
    const { t } = useTranslation();
    const actionPlan = result?.persona?.analysis_report?.action_plan || {};
    const flirtingTip = firstSentence(result?.persona?.flirting_tip);

    const guides = [
        {
            title: t('face_result.detail.guide.hair'),
            body: actionPlan.hair || t('face_result.detail.guide.hair_fallback'),
        },
        {
            title: t('face_result.detail.guide.style'),
            body: actionPlan.fashion || t('face_result.detail.guide.style_fallback'),
        },
        {
            title: t('face_result.detail.guide.profile_photo'),
            body: actionPlan.tip || flirtingTip || t('face_result.detail.guide.profile_fallback'),
        },
    ];

    return (
        <div style={slideStackStyle}>
            <div style={sectionCardStyle}>
                <div style={eyebrowStyle}>{t('face_result.detail.guide.eyebrow')}</div>
                <div style={headlineStyle}>{t('face_result.detail.guide.headline')}</div>
            </div>

            <div style={{ display: 'grid', gap: '0.75rem' }}>
                {guides.map((guide) => (
                    <div key={guide.title} style={sectionCardStyle}>
                        <div style={miniHeadlineStyle}>{guide.title}</div>
                        <p style={bodyStyle}>{guide.body}</p>
                    </div>
                ))}
            </div>

            <VibeAnalysisSection
                hasIntegrated={hasIntegrated}
                physical={physical}
                target={target}
                matchType={matchType}
                congruenceScore={congruenceScore}
                chugumiResult={chugumiResult}
                handleIntegrate={handleIntegrate}
                integrating={integrating}
            />
        </div>
    );
};

const BridgeChoiceSlide = ({ onDetailsClick, onChemistryClick }) => {
    const { t } = useTranslation();
    return (
    <div style={slideStackStyle}>
        <div style={bridgeHeroStyle}>
            <div style={bridgeHeroTopStyle}>
                <span style={bridgeChoiceEyebrowStyle}>CHEMISTRY PREVIEW</span>
                <span style={bridgeHeroBadgeStyle}>{t('face_result.detail.bridge.badge')}</span>
            </div>
            <strong style={bridgeHeroHeadlineStyle}>{t('face_result.detail.bridge.headline')}</strong>
            <p style={bridgeHeroBodyStyle}>
                {t('face_result.detail.bridge.description')}
            </p>

            <div style={bridgePreviewCardStyle}>
                <span style={bridgePreviewLabelStyle}>{t('face_result.detail.bridge.preview_label')}</span>
                <strong style={bridgePreviewTitleStyle}>{t('face_result.detail.bridge.preview_title')}</strong>
                <p style={bridgePreviewCopyStyle}>
                    {t('face_result.detail.bridge.preview_description')}
                </p>
            </div>

            <button type="button" onClick={onChemistryClick} style={bridgeHeroButtonStyle}>
                {t('face_result.detail.bridge.chemistry_cta')}
            </button>
        </div>

        <button type="button" onClick={onDetailsClick} style={bridgeChoicePrimaryStyle}>
            <span style={bridgeChoiceEyebrowStyle}>DETAILS</span>
            <strong style={bridgeChoiceTitleStyle}>{t('face_result.detail.bridge.details_cta')}</strong>
            <span style={bridgeChoiceBodyStyle}>{t('face_result.detail.bridge.details_description')}</span>
        </button>
    </div>
    );
};

const DetailSlide = ({
    result,
    hasIntegrated,
    targetCoords,
    styleFieldData,
    matchInsight,
    chugumiResult,
    handleIntegrate,
    handlePayment,
    integrating,
    reportData,
}) => (
    <div style={slideStackStyle}>
        <PersonaHero data={result?.persona} isPaid={result?.is_paid} />
        <MasculinityDetailSection result={result} />
        <VisualDNASection
            styleFieldData={styleFieldData}
            matchInsight={matchInsight}
            evidenceList={result?.persona?.analysis_report?.evidence}
            masculinityPercentile={result?.masculinity_percentile}
        />
        <VibeAnalysisSection
            hasIntegrated={hasIntegrated}
            physical={{ x: result?.physical_x, y: result?.physical_y }}
            target={targetCoords}
            matchType={result?.match_type}
            congruenceScore={result?.congruence_score}
            chugumiResult={chugumiResult}
            handleIntegrate={handleIntegrate}
            integrating={integrating}
        />
        {(hasIntegrated || result?.is_paid) ? (
            <PremiumActionSection
                data={reportData}
                isPaid={result?.is_paid}
                hasIntegrated={hasIntegrated}
                handlePayment={handlePayment}
                handleIntegrate={handleIntegrate}
            />
        ) : null}
    </div>
);

const FaceMeshVerificationCard = ({ originalImageUrl, meshImage }) => {
    if (!originalImageUrl || !meshImage) return null;

    return (
        <section className="face-mesh-card" data-html2canvas-ignore="true" aria-label="Face mesh verification">
            <div className="face-mesh-card-header">
                <span className="face-mesh-eyebrow">AI가 읽은 얼굴 좌표</span>
                <p className="face-mesh-copy">업로드한 사진에서 추출한 Face Mesh 좌표를 바탕으로 얼굴 비율과 인상 신호를 계산했습니다.</p>
            </div>
            <div className="face-mesh-grid">
                <figure className="face-mesh-figure">
                    <span className="face-mesh-label">원본 사진</span>
                    <img src={originalImageUrl} alt="업로드한 원본 얼굴 사진" className="face-mesh-image" />
                </figure>
                <figure className="face-mesh-figure face-mesh-figure-accent">
                    <span className="face-mesh-label">분석 좌표</span>
                    <img src={meshImage} alt="AI가 인식한 Face Mesh 좌표" className="face-mesh-image" />
                </figure>
            </div>
        </section>
    );
};

function normalizeFaceResult(data) {
    if (!data) return null;

    const normalized = {
        ...data,
        ...(data.summary || {}),
        ...(data.payload || {}),
    };

    if (data.payload?.result) {
        Object.assign(normalized, data.payload.result);
    }

    return normalized;
}

const FaceResultDetail = () => {
    const { id } = useParams();
    const location = useLocation();
    const navigate = useNavigate();
    const { t, i18n } = useTranslation();
    const { loaded: contentLoaded } = useContent(['face']);
    const stateResult = location.state?.result || null;
    const standalonePayload = useMemo(() => {
        if (stateResult) {
            return {
                result: stateResult,
                imagePreviewUrl: location.state?.result?.input_image_data_url || location.state?.input_image_data_url || location.state?.imagePreviewUrl || null,
                input_image_data_url: location.state?.result?.input_image_data_url || location.state?.input_image_data_url || null,
                mesh_overlay_data_url: location.state?.result?.mesh_overlay_data_url || location.state?.mesh_overlay_data_url || location.state?.result?.mesh_image || null,
            };
        }

        const storedFaceResult = readStoredFaceResult();
        if (storedFaceResult.status === 'available') {
            return storedFaceResult.data;
        }

        return null;
    }, [stateResult, location.state?.imagePreviewUrl, location.state?.input_image_data_url, location.state?.mesh_overlay_data_url]);
    const isStandaloneResult = !id && Boolean(standalonePayload?.result);

    const [result, setResult] = useState(null);
    const [chugumiResult, setChugumiResult] = useState(null);
    const [loading, setLoading] = useState(true);
    const [integrating, setIntegrating] = useState(false);
    const [error, setError] = useState(null);
    const [styleFieldData, setStyleFieldData] = useState(null);
    const [matchInsight, setMatchInsight] = useState(null);
    const [activeSlide, setActiveSlide] = useState(0);

    const localizedResult = useMemo(() => result ? {
        ...result,
        persona: localizeFacePersona(result.persona, t),
    } : null, [result, t, i18n.resolvedLanguage]);

    const feedRef = useRef(null);
    const reportRootRef = useRef(null);
    const targetSlideRef = useRef(0);
    const resultViewTrackedRef = useRef('');
    const sectionViewTrackedRef = useRef(new Set());
    const analyticsResultKey = String(result?.id || result?.result_id || id || 'standalone');

    useReportAnalytics({
        rootRef: reportRootRef,
        analysisType: 'face',
        resultKey: analyticsResultKey,
        enabled: Boolean(result && contentLoaded && !loading),
    });

    const fetchResult = async () => {
        try {
            const data = await apiFetchAuth(`/me/results/${id}`);
            const normalized = normalizeFaceResult(data);

            setResult(normalized);

            if (normalized.physical_x === undefined || normalized.physical_x === null) {
                const chugumiData = await apiFetchAuth('/me/results?test_type=chugumi&limit=1');
                if (chugumiData.results && chugumiData.results.length > 0) {
                    setChugumiResult(chugumiData.results[0]);
                }
            }
        } catch (err) {
            console.error(err);
            setError(t('face_result.ui.alert_analysis_fail', { error: err.message }));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (isStandaloneResult && !isLoggedIn()) {
            if (standalonePayload) {
                writeStoredFaceResult(standalonePayload);
            }

            requireLoginOrContinue({
                isAuthenticated: false,
                location: {
                    pathname: '/analyze',
                    search: '',
                    hash: '',
                },
                navigate,
                fallback: '/analyze',
            });
            return;
        }

        if (standalonePayload?.result) {
            setResult(normalizeFaceResult(standalonePayload.result));
            setLoading(false);
            return;
        }

        if (!id) {
            setLoading(false);
            setError(t('common.empty_record'));
            return;
        }

        fetchResult();
    }, [id, isStandaloneResult, navigate, standalonePayload, t]);

    useEffect(() => {
        if (isLoggedIn() && isStandaloneResult) {
            clearStoredFaceResult();
        }
    }, [isStandaloneResult]);

    useEffect(() => {
        if (!result || !contentLoaded || loading) return;

        const resultKey = String(result.id || result.result_id || id || 'standalone');
        if (resultViewTrackedRef.current === resultKey) return;
        resultViewTrackedRef.current = resultKey;

        trackEventOnce('result_viewed', `face:${resultKey}`, {
            feature_type: 'face',
            analysis_type: 'face',
            result_type: result.persona?.code,
            score_bucket: getScoreBucket(result.masculinity_percentile),
            is_paid_result: Boolean(result.is_paid),
            entry_source: id ? 'saved_result' : 'new_analysis',
            is_logged_in: isLoggedIn(),
        });
    }, [contentLoaded, id, loading, result]);

    useEffect(() => {
        if (!result || !result.labels) return;

        const field = calculateStyleField(result);
        if (!field) return;

        const chugumiTitle = result.chugumi_type || chugumiResult?.result_label || null;
        const targetCoord = chugumiTitle ? getChugumiCoordinate(chugumiTitle) : null;
        const identityDot = calculateIdentityDot(field.radii);
        const feasibilityMode = targetCoord ? checkTargetFeasibility(targetCoord, field.radii) : null;
        const matchStatus = targetCoord ? calculateMatchStatus(identityDot, targetCoord, field.radii) : null;
        const potential = analyzePotential(field.radii);
        const insight = matchStatus ? generateMatchInsight(matchStatus, potential) : null;
        const actionPlan = matchStatus ? generateActionPlan(matchStatus, field.radii) : null;

        setStyleFieldData({ ...field, targetCoord, identityDot, feasibilityMode });
        setMatchInsight(insight ? {
            ...insight,
            actionPlan,
            matchStatus,
            personaTitle: result.persona?.title || '',
            personaKillerCopy: result.persona?.killer_copy || '',
        } : null);
    }, [result, chugumiResult]);

    const handleIntegrate = async (isPaid = false) => {
        const faceResultId = id || result?.id || result?.result_id;
        if (!chugumiResult || !faceResultId) return;

        setIntegrating(true);
        try {
            await apiFetchAuth('/analyze/integrated', {
                method: 'POST',
                body: JSON.stringify({
                    face_result_id: faceResultId,
                    chugumi_type_code: chugumiResult.result_label,
                    is_paid: isPaid,
                }),
            });
            if (id) {
                await fetchResult();
            }
        } catch (err) {
            alert(t('face_result.ui.alert_analysis_fail', { error: err.message }));
        } finally {
            setIntegrating(false);
        }
    };

    const handleExportReport = async () => {
        try {
            const reportElement = document.getElementById('premium-report-container');
            if (!reportElement) return;

            const html2canvas = (await import('html2canvas')).default;
            const canvas = await html2canvas(reportElement, {
                backgroundColor: '#0f172a',
                scale: 2,
                logging: false,
            });

            const link = document.createElement('a');
            link.download = `Start-Us_Premium_Report_${new Date().getTime()}.png`;
            link.href = canvas.toDataURL('image/png');
            link.click();
            trackEvent('result_action_clicked', {
                analysis_type: 'face',
                action_name: 'download',
                source_section: 'detailed_report',
            });
        } catch (err) {
            console.error('Export failed:', err);
            alert(t('face_result.ui.alert_payment_fail'));
        }
    };

    const handlePayment = async () => {
        try {
            const paymentConfirmed = window.confirm(
                t('face_result.ui.alert_payment_confirm', { price: '9,900' })
            );
            if (!paymentConfirmed) return;

            await handleIntegrate(true);
            alert(t('face_result.ui.alert_payment_success'));
        } catch (err) {
            console.error('Payment failed:', err);
            alert(t('face_result.ui.alert_payment_fail'));
        }
    };

    const getTargetCoords = (typeCode) => {
        if (!typeCode || typeof typeCode !== 'string') {
            return { x: 0, y: 0 };
        }

        const parts = typeCode.split('_');
        const vibeMap = { Cuteness: -35, Softness: -12, Coolness: 12, Masculine: 35 };
        const idMap = { Practical: -35, Classic: -12, Artistic: 12, Unique: 35 };

        return {
            x: vibeMap[parts[0]] || 0,
            y: idMap[parts[1]] || 0,
        };
    };

    const hasIntegrated = result?.physical_x !== undefined && result?.physical_x !== null;

    let targetCoords = null;
    if (result?.target_x !== undefined && result?.target_x !== null) {
        targetCoords = { x: result.target_x, y: result.target_y };
    } else if (hasIntegrated && result.chugumi_type) {
        const typeForTarget = result.chugumi_type;
        targetCoords = getTargetCoords(typeForTarget);
    }

    const formattedDate =
        formatResultDate(result?.created_at || result?.timestamp) ||
        t('face_result.ui.no_date_info');

    const originalImageUrl = result?.input_image_data_url || result?.payload?.input_image_data_url || standalonePayload?.input_image_data_url || standalonePayload?.imagePreviewUrl || location.state?.input_image_data_url || location.state?.imagePreviewUrl || null;
    const meshImage = result?.mesh_overlay_data_url || result?.payload?.mesh_overlay_data_url || result?.mesh_image || result?.payload?.mesh_image || standalonePayload?.mesh_overlay_data_url || location.state?.mesh_overlay_data_url || null;

    const presentationResult = localizedResult || result;
    const reportData = {
        ...presentationResult,
        targetCoords,
        hasIntegrated,
    };

    const slides = presentationResult ? [
        {
            key: 'overview',
            label: 'Overview',
            title: t('face_result.detail.slides.overview'),
            content: <PersonaHero data={presentationResult.persona} isPaid={presentationResult.is_paid} feedMode />,
        },
        {
            key: 'impression',
            label: 'Impression',
            title: t('face_result.detail.slides.impression'),
            content: <FirstImpressionSlide result={presentationResult} />,
        },
        {
            key: 'evidence',
            label: 'Evidence',
            title: t('face_result.detail.slides.evidence'),
            content: (
                <div style={slideStackStyle}>
                    <MasculinityDetailSection result={presentationResult} compact />
                    <VisualDNASection
                        styleFieldData={styleFieldData}
                        matchInsight={matchInsight}
                        evidenceList={presentationResult.persona?.analysis_report?.evidence}
                        masculinityPercentile={presentationResult.masculinity_percentile}
                        compact
                    />
                </div>
            ),
        },
        {
            key: 'guide',
            label: 'Guide',
            title: t('face_result.detail.slides.guide'),
            content: (
                <UsageGuideSlide
                    result={presentationResult}
                    hasIntegrated={hasIntegrated}
                    physical={{ x: result.physical_x, y: result.physical_y }}
                    target={targetCoords}
                    matchType={result.match_type}
                    congruenceScore={result.congruence_score}
                    chugumiResult={chugumiResult}
                    handleIntegrate={handleIntegrate}
                    integrating={integrating}
                />
            ),
        },
        {
            key: 'details',
            label: 'Details',
            title: t('face_result.detail.slides.details'),
            content: (
                <DetailSlide
                    result={presentationResult}
                    hasIntegrated={hasIntegrated}
                    targetCoords={targetCoords}
                    styleFieldData={styleFieldData}
                    matchInsight={matchInsight}
                    chugumiResult={chugumiResult}
                    handleIntegrate={handleIntegrate}
                    handlePayment={handlePayment}
                    integrating={integrating}
                    reportData={reportData}
                />
            ),
        },
    ] : [];

    const choiceSlide = {
        key: 'choice',
        label: 'Choice',
        title: t('face_result.detail.slides.choice'),
        content: (
            <BridgeChoiceSlide
                onDetailsClick={() => {
                    trackEvent('result_action_clicked', {
                        analysis_type: 'face',
                        action_name: 'view_detail',
                        source_section: 'choice',
                    });
                    scrollToSlide(5);
                }}
                onChemistryClick={() => {
                    trackEvent('result_action_clicked', {
                        analysis_type: 'face',
                        action_name: 'start_chemistry',
                        source_section: 'choice',
                    });
                    navigate('/chemistry-test/info');
                }}
            />
        ),
    };

    const slideDeck = presentationResult ? [...slides.slice(0, 4), choiceSlide, ...slides.slice(4)] : [];
    const slideCount = slideDeck.length;

    useEffect(() => {
        const feedNode = feedRef.current;
        if (!feedNode) return undefined;

        const handleScroll = () => {
            const slideWidth = feedNode.clientWidth;
            if (!slideWidth) return;
            const nextIndex = Math.round(feedNode.scrollLeft / slideWidth);
            const boundedIndex = Math.max(0, Math.min(slideCount - 1, nextIndex));
            targetSlideRef.current = boundedIndex;
            setActiveSlide(boundedIndex);
        };

        handleScroll();
        feedNode.addEventListener('scroll', handleScroll, { passive: true });
        return () => feedNode.removeEventListener('scroll', handleScroll);
    }, [slideCount, result?.id]);

    useEffect(() => {
        targetSlideRef.current = 0;
        setActiveSlide(0);
        sectionViewTrackedRef.current = new Set();
    }, [result?.id]);

    useEffect(() => {
        if (!contentLoaded || !result) return;
        const sectionNameBySlide = {
            overview: 'summary',
            impression: 'visual_attraction',
            evidence: 'face_mesh',
            guide: 'recommendation',
            choice: 'compatibility_direction',
            details: 'detailed_report',
        };
        const sectionName = sectionNameBySlide[slideDeck[activeSlide]?.key];
        if (!sectionName || sectionViewTrackedRef.current.has(sectionName)) return;
        sectionViewTrackedRef.current.add(sectionName);
        const resultKey = String(result.id || result.result_id || id || 'standalone');
        trackEventOnce('report_section_viewed', `face:${resultKey}:${sectionName}`, {
            analysis_type: 'face',
            section_name: sectionName,
        });
    }, [activeSlide, contentLoaded, id, result, slideDeck]);

    if (loading || !contentLoaded) return <div style={styles.container}>{t('common.loading')}</div>;
    if (error) return <div style={styles.container}>{error}</div>;
    if (!result) return <div style={styles.container}>{t('common.empty_record')}</div>;

    const scrollToSlide = (index) => {
        const feedNode = feedRef.current;
        if (!feedNode) return;

        const boundedIndex = Math.max(0, Math.min(slideCount - 1, index));
        targetSlideRef.current = boundedIndex;
        setActiveSlide(boundedIndex);

        feedNode.scrollTo({
            left: feedNode.clientWidth * boundedIndex,
            behavior: 'smooth',
        });
    };

    const handlePrevSlide = () => {
        scrollToSlide(targetSlideRef.current - 1);
    };

    const handleNextSlide = () => {
        scrollToSlide(targetSlideRef.current + 1);
    };

    return (
        <div style={styles.container}>
            <InjectAnimations />

            <div ref={reportRootRef} style={resultPageStackStyle}>
                <FaceMeshVerificationCard originalImageUrl={originalImageUrl} meshImage={meshImage} />

            <div style={styles.card} id="premium-report-container">
                <header style={styles.header}>
                    <button
                        onClick={() => (isStandaloneResult ? navigate('/') : navigate('/mypage'))}
                        style={styles.backButton}
                    >
                        {isStandaloneResult ? t('face_result.detail.back_home') : `← ${t('face_result.ui.back_to_mypage')}`}
                    </button>
                    {hasIntegrated && result.is_paid ? (
                        <button onClick={handleExportReport} style={styles.exportButton}>
                            {t('face_result.ui.save_report')}
                        </button>
                    ) : null}
                </header>

                <div>
                    <h1 style={styles.title}>{presentationResult.persona?.title || presentationResult.masculinity_label || t('face_result.ui.result_title')}</h1>
                    {hasIntegrated ? (
                        <div style={{ ...styles.integratedBadge, display: 'inline-block', marginTop: '0.5rem' }}>
                            {t('face_result.ui.integrated_done')}
                        </div>
                    ) : null}
                </div>
                <p style={resultDateStyle}>
                    {t('face_result.ui.analysis_date', { date: formattedDate })}
                </p>

                <div style={feedHeaderStyle}>
                    <div>
                        <div style={feedEyebrowStyle}>Summary Feed</div>
                        <div style={feedTitleStyle}>{slideDeck[activeSlide]?.title || t('face_result.detail.slides.overview')}</div>
                    </div>
                    <div style={feedControlsStyle}>
                        <button
                            type="button"
                            onClick={handlePrevSlide}
                            disabled={activeSlide === 0}
                            style={{
                                ...feedArrowButtonStyle,
                                ...(activeSlide === 0 ? feedArrowButtonDisabledStyle : {}),
                            }}
                            aria-label="Previous slide"
                        >
                            {'<'}
                        </button>
                        <span style={feedIndexStyle}>{activeSlide + 1} / {slideCount}</span>
                        {slideDeck.map((slide, index) => (
                            <button
                                key={slide.key}
                                type="button"
                                onClick={() => scrollToSlide(index)}
                                style={{
                                    ...feedDotStyle,
                                    ...(activeSlide === index ? activeFeedDotStyle : {}),
                                }}
                                aria-label={`${slide.label} slide`}
                            />
                        ))}
                        <button
                            type="button"
                            onClick={handleNextSlide}
                            disabled={activeSlide === slideCount - 1}
                            style={{
                                ...feedArrowButtonStyle,
                                ...(activeSlide === slideCount - 1 ? feedArrowButtonDisabledStyle : {}),
                            }}
                            aria-label="Next slide"
                        >
                            {'>'}
                        </button>
                    </div>
                </div>

                <main ref={feedRef} className="face-detail-feed" style={feedTrackStyle}>
                    {slideDeck.map((slide) => (
                        <section key={slide.key} style={feedSlideStyle}>
                            <div style={feedSlideInnerStyle}>{slide.content}</div>
                        </section>
                    ))}
                </main>

                {!hasIntegrated && !chugumiResult ? (
                    <div style={{ marginTop: '1.5rem', textAlign: 'center' }}>
                        <button onClick={() => navigate('/chugumi-test')} style={styles.linkButton}>
                            {t('face_result.ui.view_chugumi')}
                        </button>
                    </div>
                ) : null}
            </div>
            </div>
        </div>
    );
};

const resultPageStackStyle = {
    width: '100%',
    maxWidth: '800px',
    display: 'flex',
    flexDirection: 'column',
    gap: '1rem',
};

const slideStackStyle = {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.75rem',
};

const sectionCardStyle = {
    background: 'rgba(255,255,255,0.03)',
    border: '1px solid rgba(255,255,255,0.06)',
    borderRadius: '18px',
    padding: '0.85rem',
};

const eyebrowStyle = {
    fontSize: '0.78rem',
    color: '#60a5fa',
    fontWeight: 800,
    letterSpacing: '0.08em',
    textTransform: 'uppercase',
    marginBottom: '0.45rem',
};

const headlineStyle = {
    color: '#f8fafc',
    fontWeight: 800,
    fontSize: '1rem',
    lineHeight: 1.4,
};

const miniHeadlineStyle = {
    color: '#f8fafc',
    fontWeight: 700,
    fontSize: '0.95rem',
};

const bodyStyle = {
    color: '#cbd5e1',
    fontSize: '0.88rem',
    lineHeight: 1.65,
    margin: '0.45rem 0 0 0',
};

const numberLabelStyle = {
    width: '28px',
    height: '28px',
    borderRadius: '999px',
    display: 'grid',
    placeItems: 'center',
    background: 'rgba(96,165,250,0.14)',
    color: '#93c5fd',
    fontWeight: 800,
    marginBottom: '0.75rem',
};

const bridgeChoiceGridStyle = {
    display: 'grid',
    gap: '0.9rem',
};

const bridgeChoiceBaseStyle = {
    textAlign: 'left',
    borderRadius: '20px',
    padding: '0.95rem',
    border: '1px solid rgba(255,255,255,0.08)',
    cursor: 'pointer',
    display: 'flex',
    flexDirection: 'column',
    gap: '0.5rem',
    transition: 'all 0.2s ease',
};

const bridgeChoicePrimaryStyle = {
    ...bridgeChoiceBaseStyle,
    background: 'rgba(255,255,255,0.04)',
    color: '#f8fafc',
};

const bridgeChoiceSecondaryStyle = {
    ...bridgeChoiceBaseStyle,
    background: 'linear-gradient(135deg, rgba(59,130,246,0.18), rgba(139,92,246,0.18))',
    border: '1px solid rgba(96,165,250,0.28)',
    color: '#f8fafc',
    boxShadow: '0 18px 28px rgba(59, 130, 246, 0.12)',
};

const bridgeHeroStyle = {
    borderRadius: '24px',
    padding: '1rem',
    background: 'linear-gradient(135deg, rgba(88, 28, 135, 0.96), rgba(124, 58, 237, 0.92) 52%, rgba(99, 102, 241, 0.88))',
    border: '1px solid rgba(196, 181, 253, 0.28)',
    boxShadow: '0 26px 50px rgba(76, 29, 149, 0.34)',
    display: 'flex',
    flexDirection: 'column',
    gap: '0.9rem',
};

const bridgeHeroTopStyle = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: '0.75rem',
    flexWrap: 'wrap',
};

const bridgeHeroBadgeStyle = {
    display: 'inline-flex',
    alignItems: 'center',
    minHeight: '28px',
    padding: '0.35rem 0.7rem',
    borderRadius: '999px',
    background: 'rgba(255,255,255,0.14)',
    color: '#f5f3ff',
    fontSize: '0.78rem',
    fontWeight: '800',
    border: '1px solid rgba(255,255,255,0.18)',
};

const bridgeHeroHeadlineStyle = {
    color: '#ffffff',
    fontSize: '1.3rem',
    lineHeight: 1.35,
    fontWeight: '900',
};

const bridgeHeroBodyStyle = {
    color: 'rgba(245, 243, 255, 0.92)',
    fontSize: '0.95rem',
    lineHeight: 1.68,
    margin: 0,
};

const bridgePreviewCardStyle = {
    borderRadius: '20px',
    padding: '0.85rem',
    background: 'rgba(15, 23, 42, 0.22)',
    border: '1px solid rgba(255,255,255,0.14)',
    display: 'grid',
    gap: '0.4rem',
};

const bridgePreviewLabelStyle = {
    color: '#ddd6fe',
    fontSize: '0.74rem',
    fontWeight: '800',
    letterSpacing: '0.12em',
    textTransform: 'uppercase',
};

const bridgePreviewTitleStyle = {
    color: '#ffffff',
    fontSize: '1.05rem',
    lineHeight: 1.5,
    fontWeight: '800',
};

const bridgePreviewCopyStyle = {
    color: 'rgba(237, 233, 254, 0.88)',
    fontSize: '0.9rem',
    lineHeight: 1.6,
    margin: 0,
};

const bridgeHeroButtonStyle = {
    minHeight: '56px',
    borderRadius: '16px',
    border: 'none',
    background: '#ffffff',
    color: '#5b21b6',
    fontSize: '1rem',
    fontWeight: '900',
    cursor: 'pointer',
    boxShadow: '0 16px 28px rgba(255,255,255,0.16)',
    padding: '0.95rem 1rem',
};

const bridgeChoiceEyebrowStyle = {
    fontSize: '0.73rem',
    letterSpacing: '0.12em',
    textTransform: 'uppercase',
    color: '#93c5fd',
    fontWeight: '800',
};

const bridgeChoiceTitleStyle = {
    fontSize: '1rem',
    fontWeight: '800',
    color: '#ffffff',
};

const bridgeChoiceBodyStyle = {
    fontSize: '0.9rem',
    lineHeight: 1.6,
    color: '#cbd5e1',
};

const feedHeaderStyle = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: '1rem',
    marginBottom: '0.65rem',
    flexWrap: 'wrap',
};

const feedEyebrowStyle = {
    fontSize: '0.75rem',
    letterSpacing: '0.14em',
    textTransform: 'uppercase',
    color: '#60a5fa',
    fontWeight: '800',
    marginBottom: '0.3rem',
};

const feedTitleStyle = {
    fontSize: '1.15rem',
    fontWeight: '700',
    color: '#f8fafc',
};

const feedControlsStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    flexWrap: 'wrap',
};

const feedArrowButtonStyle = {
    width: '34px',
    height: '34px',
    borderRadius: '999px',
    border: '1px solid rgba(255,255,255,0.12)',
    background: 'rgba(15, 23, 42, 0.72)',
    color: '#f8fafc',
    cursor: 'pointer',
    fontSize: '1rem',
    fontWeight: '700',
    display: 'grid',
    placeItems: 'center',
    transition: 'all 0.2s ease',
};

const feedArrowButtonDisabledStyle = {
    opacity: 0.35,
    cursor: 'not-allowed',
};

const feedIndexStyle = {
    minWidth: '48px',
    textAlign: 'center',
    color: '#94a3b8',
    fontSize: '0.84rem',
    fontWeight: '700',
};

const feedDotStyle = {
    width: '10px',
    height: '10px',
    borderRadius: '999px',
    border: 'none',
    background: 'rgba(148, 163, 184, 0.35)',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    padding: 0,
};

const activeFeedDotStyle = {
    width: '28px',
    background: 'linear-gradient(90deg, #60a5fa, #a78bfa)',
};

const feedTrackStyle = {
    display: 'flex',
    gap: '1rem',
    overflowX: 'auto',
    scrollSnapType: 'x mandatory',
    scrollBehavior: 'smooth',
    WebkitOverflowScrolling: 'touch',
    msOverflowStyle: 'none',
    scrollbarWidth: 'none',
    margin: '0 -0.25rem',
    padding: '0 0.25rem 0.25rem',
};

const feedSlideStyle = {
    flex: '0 0 100%',
    width: '100%',
    scrollSnapAlign: 'start',
    scrollSnapStop: 'always',
};

const feedSlideInnerStyle = {};

const resultDateStyle = {
    color: '#94a3b8',
    margin: '0.35rem 0 1rem',
    fontSize: '0.9rem',
};

export default FaceResultDetail;
